import { audioContext, isAdMuted, onAdMuteChange } from './sound';
import { useSettings } from '../store/settings';

/**
 * Background music, synthesized with Web Audio: no files to download or license.
 * A small look-ahead sequencer schedules notes ~120 ms ahead on the audio clock,
 * so timing stays tight even when the main thread is busy with the game.
 *
 * Two moods: a calm loop for menus and an upbeat one in matches. Switching mood
 * crossfades; the music pauses while the tab is hidden.
 */
export type Mood = 'menu' | 'game';

interface Track {
  bpm: number;
  /** One chord per bar: root note (MIDI) and chord tones as semitone offsets. */
  bars: Array<{ root: number; tones: number[] }>;
  /** 16 steps per bar. */
  kick: number[];
  hat: number[];
  /** Arpeggio pattern: index into the chord tones per 16th (-1 = rest). */
  arp: number[];
  /** Bass hits (16ths). */
  bass: number[];
  pad: boolean;
}

const MINOR = [0, 3, 7, 12];
const MAJOR = [0, 4, 7, 12];

const TRACKS: Record<Mood, Track> = {
  menu: {
    bpm: 92,
    bars: [
      { root: 57, tones: MINOR }, // Am
      { root: 53, tones: MAJOR }, // F
      { root: 48, tones: MAJOR }, // C
      { root: 55, tones: MAJOR }, // G
    ],
    kick: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0],
    arp: [0, -1, 1, -1, 2, -1, 3, -1, 2, -1, 1, -1, 2, -1, 1, -1],
    bass: [1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0],
    pad: true,
  },
  game: {
    bpm: 124,
    bars: [
      { root: 52, tones: MINOR }, // Em
      { root: 48, tones: MAJOR }, // C
      { root: 55, tones: MAJOR }, // G
      { root: 50, tones: MAJOR }, // D
    ],
    kick: [1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0, 1, 0, 0, 0],
    hat: [0, 0, 1, 0, 0, 0, 1, 1, 0, 0, 1, 0, 0, 0, 1, 0],
    arp: [0, 2, 1, 3, 0, 2, 1, 3, 0, 2, 1, 3, 2, 1, 3, 2],
    bass: [1, 0, 1, 0, 1, 0, 1, 1, 1, 0, 1, 0, 1, 0, 1, 1],
    pad: false,
  },
};

const hz = (midi: number) => 440 * 2 ** ((midi - 69) / 12);
const LOOKAHEAD_S = 0.12;
const TICK_MS = 25;

class MusicPlayer {
  private bus: GainNode | null = null;
  private filter: BiquadFilterNode | null = null;
  private noise: AudioBuffer | null = null;
  private mood: Mood | null = null;
  private step = 0;
  private nextAt = 0;
  private timer: number | null = null;

  private ac(): AudioContext | null {
    const ac = audioContext();
    if (!ac) return null;
    if (!this.bus) {
      this.bus = ac.createGain();
      this.bus.gain.value = 0;
      // A gentle low-pass keeps the synths warm and out of the way of sound effects.
      this.filter = ac.createBiquadFilter();
      this.filter.type = 'lowpass';
      this.filter.frequency.value = 3200;
      this.bus.connect(this.filter).connect(ac.destination);
      const len = Math.floor(ac.sampleRate * 0.2);
      this.noise = ac.createBuffer(1, len, ac.sampleRate);
      const d = this.noise.getChannelData(0);
      for (let i = 0; i < len; i++) d[i] = Math.random() * 2 - 1;
    }
    return ac;
  }

  /** Target loudness from settings (0 when muted). */
  private level(): number {
    const s = useSettings.getState();
    return s.sound && s.music && !isAdMuted() ? 0.22 * s.musicVolume : 0;
  }

  /** Start (or switch to) a mood. Must first be called after a user gesture (autoplay rules). */
  play(mood: Mood): void {
    const ac = this.ac();
    if (!ac || !this.bus) return;
    if (this.mood !== mood) {
      this.mood = mood;
      this.step = 0;
      this.nextAt = ac.currentTime + 0.1;
    }
    this.applyVolume(0.6);
    if (this.timer === null) this.timer = window.setInterval(() => this.schedule(), TICK_MS);
  }

  /** Fade out and stop scheduling. */
  stop(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    this.applyVolume(0.3, 0);
  }

  applyVolume(fade = 0.3, target = this.level()): void {
    const ac = audioContext();
    if (!ac || !this.bus) return;
    const g = this.bus.gain;
    g.cancelScheduledValues(ac.currentTime);
    g.setValueAtTime(g.value, ac.currentTime);
    g.linearRampToValueAtTime(target, ac.currentTime + fade);
  }

  get playing(): boolean {
    return this.timer !== null;
  }

  private schedule(): void {
    const ac = audioContext();
    if (!ac || !this.mood) return;
    const track = TRACKS[this.mood];
    const stepDur = 60 / track.bpm / 4;
    // After a long pause (tab hidden, slow device) don't try to catch up on missed notes.
    if (this.nextAt < ac.currentTime - 0.2) this.nextAt = ac.currentTime + 0.05;
    while (this.nextAt < ac.currentTime + LOOKAHEAD_S) {
      this.playStep(track, this.step, this.nextAt, stepDur);
      this.step = (this.step + 1) % (track.bars.length * 16);
      this.nextAt += stepDur;
    }
  }

  private playStep(track: Track, step: number, t: number, stepDur: number): void {
    const bar = track.bars[Math.floor(step / 16)]!;
    const s = step % 16;
    if (track.pad && s === 0) for (const tone of bar.tones.slice(0, 3)) this.voice(hz(bar.root + tone), t, stepDur * 15, 'triangle', 0.08, 0.35);
    if (track.bass[s]) this.voice(hz(bar.root - 12), t, stepDur * 1.6, 'sawtooth', 0.12, 0.01);
    const a = track.arp[s]!;
    if (a >= 0) this.voice(hz(bar.root + 12 + bar.tones[a % bar.tones.length]!), t, stepDur * 0.9, 'square', 0.035, 0.005);
    if (track.kick[s]) this.kick(t);
    if (track.hat[s]) this.hat(t);
  }

  private voice(freq: number, t: number, dur: number, type: OscillatorType, vol: number, attack: number): void {
    const ac = audioContext()!;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.type = type;
    o.frequency.setValueAtTime(freq, t);
    g.gain.setValueAtTime(0.0001, t);
    g.gain.exponentialRampToValueAtTime(vol, t + Math.max(0.005, attack));
    g.gain.exponentialRampToValueAtTime(0.0001, t + dur);
    o.connect(g).connect(this.bus!);
    o.start(t);
    o.stop(t + dur + 0.05);
  }

  private kick(t: number): void {
    const ac = audioContext()!;
    const o = ac.createOscillator();
    const g = ac.createGain();
    o.frequency.setValueAtTime(140, t);
    o.frequency.exponentialRampToValueAtTime(42, t + 0.14);
    g.gain.setValueAtTime(0.35, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.18);
    o.connect(g).connect(this.bus!);
    o.start(t);
    o.stop(t + 0.2);
  }

  private hat(t: number): void {
    const ac = audioContext()!;
    const src = ac.createBufferSource();
    src.buffer = this.noise;
    const hp = ac.createBiquadFilter();
    hp.type = 'highpass';
    hp.frequency.value = 7000;
    const g = ac.createGain();
    g.gain.setValueAtTime(0.06, t);
    g.gain.exponentialRampToValueAtTime(0.0001, t + 0.05);
    src.connect(hp).connect(g).connect(this.bus!);
    src.start(t);
    src.stop(t + 0.06);
  }
}

export const music = new MusicPlayer();
onAdMuteChange(() => music.applyVolume(0.2));

/** Pick the mood for a route: upbeat in a match or single-player run, calm everywhere else. */
export const moodFor = (path: string): Mood => (path === '/game' || path === '/practice' || path === '/solo' ? 'game' : 'menu');
